import { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
	Button,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/primitives";
import { AlertCircle, ArrowLeft } from "lucide-react";
import {
	ReflectionAnalysis,
	ReflectionAnalysisRef,
} from "@/components/ReflectionAnalysis";
import {
	ReflectionHeader,
	ReflectionSummary,
	ReflectionStates,
	QAHistory,
	ContributingFactorsCard,
	SignalsCard,
	CorrectiveActionsCard,
	EmptyState,
	ArchiveDialogs,
	PrintHeader,
	PrintFooter,
	PrintAnalysisDialog,
} from "./components";
import {
	useReflectionData,
	useReflectionActions,
	useReflectionTab,
} from "./hooks";
import { PRINT_STYLES } from "./constants";

export function ReflectionDetail() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { profile } = useAuth();
	const reflectionAnalysisRef = useRef<ReflectionAnalysisRef>(null);
	const [includeAnalysisInPrint, setIncludeAnalysisInPrint] = useState(false);
	const [showPrintAnalysisDialog, setShowPrintAnalysisDialog] = useState(false);
	const [pendingPrintAction, setPendingPrintAction] = useState<
		(() => void) | null
	>(null);
	const printTriggerRef = useRef<{
		action: () => void;
		includeAnalysis: boolean;
	} | null>(null);
	const [printTriggerKey, setPrintTriggerKey] = useState(0); // Force effect to run

	// Inject print styles into document
	useEffect(() => {
		const styleId = "reflection-print-styles";
		// Remove existing style if present
		const existingStyle = document.getElementById(styleId);
		if (existingStyle) {
			existingStyle.remove();
		}
		// Add print styles
		const styleElement = document.createElement("style");
		styleElement.id = styleId;
		styleElement.textContent = PRINT_STYLES;
		document.head.appendChild(styleElement);
		console.log("Print styles injected, length:", PRINT_STYLES.length);

		return () => {
			const style = document.getElementById(styleId);
			if (style) {
				style.remove();
			}
		};
	}, []);

	// Effect to trigger print after state update
	useEffect(() => {
		if (printTriggerRef.current) {
			const { action, includeAnalysis } = printTriggerRef.current;
			printTriggerRef.current = null;

			if (includeAnalysis) {
				// Wait for DOM to update when including analysis
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						setTimeout(() => {
							// Directly manipulate DOM visibility before print
							const tabsWrapper = document.getElementById("tabs-wrapper");
							const combinedContent = document.getElementById("print-combined");

							console.log("DOM elements found:", {
								tabsWrapper: !!tabsWrapper,
								combinedContent: !!combinedContent,
							});

							if (tabsWrapper) {
								tabsWrapper.classList.add("print-hidden");
								console.log("Tabs wrapper hidden");
							}
							if (combinedContent) {
								combinedContent.classList.add("print-visible");
								console.log(
									"Combined content shown, innerHTML length:",
									combinedContent.innerHTML.length,
								);
							}

							console.log("Triggering print with analysis included");
							action();
						}, 500); // Increased delay to ensure content is loaded
					});
				});
			} else {
				// If analysis is not included, wait for dialog to close then print
				setTimeout(() => {
					console.log("Triggering print without analysis");
					action();
				}, 300); // Increased delay to allow dialog to close
			}
		}
	}, [printTriggerKey, includeAnalysisInPrint]);

	const {
		reflection,
		contributingFactors,
		signals,
		correctiveActions,
		loading,
		error,
		setReflection,
	} = useReflectionData(id, profile?.id);

	const {
		archiveLoading,
		showArchiveDialog,
		setShowArchiveDialog,
		showUnarchiveDialog,
		setShowUnarchiveDialog,
		handleArchive,
		handleUnarchive,
	} = useReflectionActions({
		reflectionId: id,
		userId: profile?.id,
		setReflection,
	});

	const { activeTab } = useReflectionTab();

	const hasNoDetails =
		contributingFactors.length === 0 &&
		signals.length === 0 &&
		correctiveActions.length === 0;

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-8 max-w-7xl">
					<div className="flex items-center justify-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
					</div>
				</div>
			</div>
		);
	}

	if (error || !reflection) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-8 max-w-7xl">
					<div className="text-center py-8">
						<AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
						<h2 className="text-xl font-semibold text-foreground mb-2">
							{error || "Reflection not found"}
						</h2>
						<p className="text-muted-foreground mb-4">
							The reflection you're looking for doesn't exist or you don't have
							permission to view it.
						</p>
						<Button onClick={() => navigate("/reflections")} variant="outline">
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back to Reflections
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl space-y-6 print-content">
				<PrintHeader
					reflectionTitle={reflection.title}
					createdDate={reflection.created_at}
				/>

				<ReflectionHeader
					reflection={reflection}
					onBack={() => navigate("/reflections")}
					onArchive={() => setShowArchiveDialog(true)}
					onUnarchive={() => setShowUnarchiveDialog(true)}
					archiveLoading={archiveLoading}
					reflectionAnalysisRef={reflectionAnalysisRef}
					onIncludeAnalysisInPrint={setIncludeAnalysisInPrint}
					onShowPrintDialog={setShowPrintAnalysisDialog}
					onSetPendingPrintAction={setPendingPrintAction}
				/>

				{/* Normal interactive tabs - hidden during print when combining */}
				<div id="tabs-wrapper">
					<Tabs defaultValue={activeTab} className="w-full">
						<div className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg p-2 mb-6 no-print">
							<TabsList className="grid w-full grid-cols-2">
								<TabsTrigger value="reflection-report">
									Reflection Report
								</TabsTrigger>
								<TabsTrigger value="checkin-analysis">
									Check-in Analysis
								</TabsTrigger>
							</TabsList>
						</div>

						<TabsContent value="reflection-report" className="space-y-6">
							<ReflectionSummary reflection={reflection} />
							<ReflectionStates reflection={reflection} />
							<QAHistory qaResults={reflection.qa_results} />
							<ContributingFactorsCard factors={contributingFactors} />
							<SignalsCard signals={signals} />
							<CorrectiveActionsCard actions={correctiveActions} />
							<EmptyState show={hasNoDetails} />
						</TabsContent>

						<TabsContent value="checkin-analysis">
							<ReflectionAnalysis
								ref={reflectionAnalysisRef}
								reflectionId={reflection.id}
								reflectionTitle={reflection.title}
								reflectionDescription={reflection.description}
							/>
						</TabsContent>
					</Tabs>
				</div>

				{/* Combined print content - always rendered but hidden, shown only when printing with analysis */}
				<div id="print-combined">
					{/* Reflection Report Section */}
					<div className="print-report-section space-y-6">
						<ReflectionSummary reflection={reflection} />
						<ReflectionStates reflection={reflection} />
						<QAHistory qaResults={reflection.qa_results} />
						<ContributingFactorsCard factors={contributingFactors} />
						<SignalsCard signals={signals} />
						<CorrectiveActionsCard actions={correctiveActions} />
						<EmptyState show={hasNoDetails} />
					</div>

					{/* Analysis Section */}
					<div className="print-analysis-section">
						<ReflectionAnalysis
							reflectionId={reflection.id}
							reflectionTitle={reflection.title}
							reflectionDescription={reflection.description}
						/>
					</div>
				</div>

				<ArchiveDialogs
					showArchiveDialog={showArchiveDialog}
					showUnarchiveDialog={showUnarchiveDialog}
					onArchiveConfirm={handleArchive}
					onArchiveCancel={() => setShowArchiveDialog(false)}
					onUnarchiveConfirm={handleUnarchive}
					onUnarchiveCancel={() => setShowUnarchiveDialog(false)}
				/>

				<PrintAnalysisDialog
					open={showPrintAnalysisDialog}
					onOpenChange={(open) => {
						if (!open) {
							// User closed dialog without selecting (e.g., clicked outside or pressed ESC)
							setShowPrintAnalysisDialog(false);
							setIncludeAnalysisInPrint(false);
							setPendingPrintAction(null);
						} else {
							setShowPrintAnalysisDialog(open);
						}
					}}
					onYes={() => {
						setShowPrintAnalysisDialog(false);
						if (pendingPrintAction) {
							const action = pendingPrintAction;
							setPendingPrintAction(null);
							// Store the action with flag to trigger after state update
							printTriggerRef.current = { action, includeAnalysis: true };
							// Set state and trigger key to ensure useEffect runs
							setIncludeAnalysisInPrint(true);
							setPrintTriggerKey((prev) => prev + 1);
						}
					}}
					onNo={() => {
						setShowPrintAnalysisDialog(false);
						if (pendingPrintAction) {
							const action = pendingPrintAction;
							setPendingPrintAction(null);
							// Store action with flag to trigger after state update
							printTriggerRef.current = { action, includeAnalysis: false };
							// Set state and trigger key to ensure useEffect runs
							setIncludeAnalysisInPrint(false);
							setPrintTriggerKey((prev) => prev + 1);
						}
					}}
				/>

				<PrintFooter
					reflectionTitle={reflection.title}
					createdDate={reflection.created_at}
				/>
			</div>
		</div>
	);
}
